import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, ExploreDataObj, ExploreFormData } from '@pubstack/common/src/analytics/query'
import { currencies } from '@pubstack/common/src/currency'
import { useAtom, useAtomValue } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useMemo } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { contextState, currentScopeState } from '~/state'
import { filterDimensionsByUserFeatures, getTimePresets, onBreakdownRowClick } from '~/utils/analytics'
import { getExploreFormulasConfig } from './explore'
import { PureExploreWidget } from './PureExploreWidget'

const DIMENSIONS: Dimension[] = ['tagId', 'bidder', 'adUnit', 'device', 'country', 'size', 'pubstackRefresh', 'abTestPopulation', 'stackIdVersion']
const DASHBOARD: AnalyticsQueryDashboard = { name: 'explore', filterType: 'auctions' }

export const ExplorePage: FunctionComponent = () => {
  const user = useAuthenticatedUser()
  const breadcrumbs = useBreadcrumbs()
  const today = DateTime.now().startOf('day')
  const timePresets = getTimePresets(today)
  const [context, setContext] = useAtom(contextState)

  const [formData, setFormData] = useLocalStorage<ExploreFormData>({
    key: 'exploreViewDashboardDataV3',
    defaultValue: {
      metrics: [],
      dimension: 'tagId',
      type: { dimension: 'time', seriesType: 'bars' },
      limit: 10,
      order: 'DESC',
      others: true,
      stacked: true,
    },
  })
  const exploreFormulasConfig = useMemo(() => getExploreFormulasConfig(user), [user])
  const analyticsQueryTime = useAnalyticsQueryTanstack<ExploreDataObj<any>>(
    `explore.metrics.by.${formData.type.dimension}`,
    DIMENSIONS,
    context,
    { ...getDataChartsParameters(formData), limit: 10 },
    false,
    formData.metrics.length > 0
  )

  const analyticsQuerybyDim = useAnalyticsQueryTanstack<ExploreDataObj<any>>(
    `explore.metrics.by.dim`,
    DIMENSIONS,
    context,
    {
      ...getDataChartsParameters(formData),
      type: { dimension: 'dim', seriesType: 'bars' },
      limit: 10,
    },
    true,
    formData.metrics.length > 0
  )
  const currentScope = useAtomValue(currentScopeState)
  const currencySymbol = currencies[currentScope.currencyCode]

  function getDataChartsParameters(data: ExploreFormData) {
    const parameters: ExploreFormData = {
      ...data,
      metrics: data.metrics.map((d) => Object.entries(exploreFormulasConfig).find(([, { formula }]) => formula.name === d)?.[0] ?? ''),
    }
    return parameters
  }

  return (
    <AnalyticsPage onRefreshClick={() => setContext({ ...context })} timePresets={timePresets} filterDimensions={DIMENSIONS} dashboard={DASHBOARD} comparedTo={true} title={breadcrumbs}>
      <PureExploreWidget
        onSubmit={(data: ExploreFormData) => setFormData(data)}
        rawData={analyticsQueryTime.data}
        rawDataBreakdown={analyticsQuerybyDim.data}
        formData={formData}
        exploreFormulasConfig={exploreFormulasConfig}
        dimensions={filterDimensionsByUserFeatures(DIMENSIONS, user)}
        isLoading={analyticsQueryTime.isFetching}
        error={analyticsQueryTime.isError}
        empty={!analyticsQueryTime.data || !Object.keys(analyticsQueryTime?.data?.mappings)?.length}
        onRefreshClick={() => {
          /**/
        }}
        currencySymbol={currencySymbol}
        onBreakdownRowClick={onBreakdownRowClick(formData.dimension as Dimension, setContext, !analyticsQuerybyDim.data)}
        onBreakdownSortChange={() => {}}
      />
    </AnalyticsPage>
  )
}
